import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { CompanyAvatar } from '@/components/CompanyAvatar';
import {
  CheckSquare,
  Square,
  Star,
  XCircle,
  Clock,
  AlertTriangle,
  Tag,
} from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { labelColor, scoreToLabel } from '@/lib/scoring/score-display';
import { IRSScorePill } from '@/components/score/atoms/IRSScorePill';
import { useThemeColors } from '@/lib/useThemeColors';
import { StatusBadge } from '@/components/ui';
import { CardActionButton } from './CardActionButton';
import { CardExpandChevron } from './CardExpandChevron';
import type { Submission, SubmissionStatus } from '@/lib/supabase/submissions';
import { useSubmissionScoreEvent, submissionTags, submissionLogoUrl, submissionLogoIsKnockout, heldForClosedCompany } from '@/lib/supabase/submissions';
import { DIMENSION_LABELS } from '@/lib/scoring/methodology';
import { useResponsive } from '@/lib/useResponsive';
import { APP_LOCALE } from '@/lib/format';

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

function titleCaseId(id: string): string {
  return id
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ============================================================================
// Helpers
// ============================================================================

const formatAskAmount = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
};

function getStatusConfig(status: SubmissionStatus, colors: ReturnType<typeof useThemeColors>) {
  const fallback = { label: 'Unknown', color: colors.text.tertiary, bg: colors.light[300] };
  const map: Record<SubmissionStatus, { label: string; color: string; bg: string }> = {
    pending: { label: 'Pending', color: colors.text.tertiary, bg: colors.light[300] },
    scoring: { label: 'Scoring', color: colors.warning, bg: colors.amber.light },
    scored: { label: 'Scored', color: colors.primary[600], bg: colors.primary[50] },
    shortlisted: { label: 'Shortlisted', color: colors.success, bg: colors.traffic.greenLight },
    archived: { label: 'Archived', color: colors.text.tertiary, bg: colors.light[300] },
    rejected: { label: 'Rejected', color: colors.error, bg: colors.red.light },
    unscorable: { label: 'Not Scored', color: colors.warning, bg: colors.amber.light },
  };
  // Defensive: submissions.status is a CHECK-constrained text column that can
  // drift ahead of this map (it did — `claimed` crashed the whole inbox with
  // "cannot read property 'label' of undefined"). Never let an unmapped status
  // take down the list; fall back to a neutral badge.
  return (map as Record<string, typeof fallback>)[status] ?? fallback;
}

function getExpiryInfo(expiresAt: string | null): { text: string; isUrgent: boolean; isExpired: boolean } | null {
  if (!expiresAt) return null;
  const now = Date.now();
  const expiryDate = new Date(expiresAt).getTime();
  const daysLeft = Math.ceil((expiryDate - now) / (24 * 60 * 60 * 1000));

  if (daysLeft < 0) return { text: 'Expired', isUrgent: true, isExpired: true };
  if (daysLeft <= 7) return { text: `Expires in ${daysLeft}d`, isUrgent: true, isExpired: false };
  return { text: `Expires in ${daysLeft}d`, isUrgent: false, isExpired: false };
}

/**
 * When the deck actually arrived, as a date AND a clock time.
 *
 * `getRelativeTime` below still exists and still reads well at a glance, but
 * "3 days ago" is not an answer to "when did this come in?" — an investor
 * triaging an inbox needs to place a submission against a meeting, a call or
 * another deck, and a relative string cannot do that. Both are shown: the
 * absolute stamp is the fact, the relative one is the feel.
 *
 * Device locale and timezone deliberately — the stamp means the moment it
 * arrived FOR THE READER, and a fixed locale would render Swiss users a format
 * they do not use.
 */
function formatSubmittedAt(dateStr: string, form: 'full' | 'compact' = 'full'): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';

  // The year is noise on a card until it is not this year. Dropping it buys the
  // compact layout the room to show a clock time at all, which is the part an
  // investor is actually placing against a meeting.
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const showYear = form === 'full' || !sameYear;

  const date = d.toLocaleDateString(APP_LOCALE, {
    day: 'numeric',
    month: 'short',
    ...(showYear ? { year: 'numeric' as const } : {}),
  });
  const time = d.toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

function getRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

// ============================================================================
// Props
// ============================================================================

interface SubmissionCardProps {
  submission: Submission;
  index: number;
  layout: 'list' | 'grid';
  isExpanded: boolean;
  onToggleExpanded: (id: string) => void;
  isSelected: boolean;
  isSelectionMode: boolean;
  onToggleSelection: (id: string) => void;
  onViewDetail: (id: string) => void;
  onShortlist: (id: string) => void;
  onReject: (id: string) => void;
  onArchive: (id: string) => void;
}

// ============================================================================
// Score Mini-Bar
// ============================================================================

function ScoreMiniBar({ label, score, colors }: {
  label: string;
  score: number;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const barColor = labelColor(scoreToLabel(score));
  return (
    <View className="flex-row items-center gap-2 mb-1.5">
      <Text style={{ color: colors.text.tertiary }} className="text-xs w-16">{label}</Text>
      <View className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.light[300] }}>
        <View
          className="h-full rounded-full"
          style={{ backgroundColor: barColor, width: `${Math.min(score, 100)}%` }}
        />
      </View>
      <Text style={{ color: colors.text.secondary }} className="text-xs font-medium w-7 text-right">{score}</Text>
    </View>
  );
}

// ============================================================================
// Card Score Breakdown — real per-dimension bars from the canonical score_event
// ============================================================================
//
// Replaces the dead `ai_scoring_data` mini-bars (that JSONB column was dropped
// in Phase 116.6). Mounted only inside the expanded card body, so the fetch is
// lazy. Renders the top dimensions + top risk from the linked score_event.

function CardScoreBreakdown({
  startupProfileId,
  variant,
  colors,
}: {
  startupProfileId: string;
  variant: 'bars' | 'inline';
  colors: ReturnType<typeof useThemeColors>;
}) {
  const { data: event } = useSubmissionScoreEvent(startupProfileId);
  if (!event || event.dimensions.length === 0) return null;

  const topDims = [...event.dimensions].sort((a, b) => b.rawScore - a.rawScore).slice(0, 5);
  const dimLabel = (id: string) =>
    DIMENSION_LABELS[id as keyof typeof DIMENSION_LABELS] ?? titleCaseId(id);

  if (variant === 'inline') {
    return (
      <View className="flex-row flex-wrap gap-2 mb-3">
        {topDims.slice(0, 3).map((d) => (
          <View key={d.id} className="flex-row items-center gap-1">
            <Text style={{ color: colors.text.tertiary }} className="text-xs">{dimLabel(d.id)}:</Text>
            <Text style={{ color: labelColor(scoreToLabel(d.rawScore)) }} className="text-xs font-bold">{d.rawScore}</Text>
          </View>
        ))}
      </View>
    );
  }

  const topRisk = [...event.risks].sort(
    (a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9),
  )[0];

  return (
    <>
      <View className="mb-3 p-3 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
        <Text style={{ color: colors.text.primary }} className="text-xs font-semibold mb-2 uppercase tracking-wider">
          Score Breakdown
        </Text>
        {topDims.map((d) => (
          <ScoreMiniBar key={d.id} label={dimLabel(d.id)} score={d.rawScore} colors={colors} />
        ))}
      </View>

      {topRisk && (
        <View className="flex-row items-start gap-2 mb-3 px-2 py-2 rounded-lg" style={{ backgroundColor: colors.red.light }}>
          <AlertTriangle size={14} color={colors.error} style={{ marginTop: 1 }} />
          <Text style={{ color: colors.error }} className="text-xs flex-1" numberOfLines={2}>
            <Text className="font-semibold">{titleCaseId(topRisk.category)}</Text>
            {` (${topRisk.severity}) — ${topRisk.description}`}
          </Text>
        </View>
      )}
    </>
  );
}

// ============================================================================
// Component
// ============================================================================

export function SubmissionCard({
  submission,
  index,
  layout,
  isExpanded,
  onToggleExpanded,
  isSelected,
  isSelectionMode,
  onToggleSelection,
  onViewDetail,
  onShortlist,
  onReject,
  onArchive,
}: SubmissionCardProps) {
  const colors = useThemeColors();
  const { isDesktop } = useResponsive();
  // A deck held because it matches a closed company (119-03) is not merely
  // "Not Scored": it waits for the investor's decision, so it says so.
  const heldFor = heldForClosedCompany(submission);
  const statusCfg = heldFor
    ? { label: 'Matches a closed company', color: colors.warning, bg: colors.amber.light }
    : getStatusConfig(submission.status, colors);
  // Informational tags (Claimed/Invited) are derived from structured data and
  // shown ALONGSIDE the scoring status — a scored deck that was claimed reads
  // "Scored · Claimed", not just "Claimed".
  const tags = submissionTags(submission);
  const expiry = getExpiryInfo(submission.expires_at);

  // Selection checkbox
  const selectionCheckbox = isSelectionMode ? (
    <AnimatedPressable
      onPress={() => onToggleSelection(submission.id)}
      accessibilityRole="checkbox"
      accessibilityLabel={`Select ${submission.company_name}`}
      accessibilityState={{ checked: isSelected }}
      className="mr-3"
    >
      {isSelected ? (
        <CheckSquare size={20} color={colors.primary[600]} />
      ) : (
        <Square size={20} color={colors.text.tertiary} />
      )}
    </AnimatedPressable>
  ) : null;

  // Score pill
  // Venture band is derived from ai_score via the same boundaries the canonical
  // composite.ventureLabel uses (81/66/46/26), so it matches the full score_event
  // exactly — no fetch needed for the always-visible headline pill.
  const scorePill = submission.ai_score !== null ? (
    <IRSScorePill score={submission.ai_score} />
  ) : null;

  // Confidence badge
  const confidenceBadge = submission.ai_confidence !== null ? (
    <View
      className="px-1.5 py-0.5 rounded"
      style={{ backgroundColor: colors.surface.secondary }}
    >
      <Text style={{ color: colors.text.tertiary }} className="text-[10px] font-medium">
        {submission.ai_confidence}% conf
      </Text>
    </View>
  ) : null;

  // ============================================================================
  // LIST MODE
  // ============================================================================
  if (layout === 'list') {
    return (
      <Animated.View entering={FadeIn.delay(50 + index * 20).duration(200)}>
        <View
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: isSelected ? 2 : 1,
            borderColor: isSelected ? colors.primary[600] : colors.border.light,
          }}
        >
          {/* Header Row — tapping the body opens the full detail modal; the
              chevron toggles the inline preview (peek) without leaving the list. */}
          <View className="p-3 flex-row items-center">
            {selectionCheckbox}
            <AnimatedPressable
              onPress={() => (isSelectionMode ? onToggleSelection(submission.id) : onViewDetail(submission.id))}
              accessibilityRole="button"
              accessibilityLabel={`${submission.company_name}, ${submission.ai_extracted_data?.sector ?? 'Unknown sector'}, score ${submission.ai_score ?? 'pending'}. Open full details`}
              className="flex-1 flex-row items-center"
            >
              <View style={{ marginRight: 12 }}>
                <CompanyAvatar
                  name={submission.company_name}
                  logoUrl={submissionLogoUrl(submission)}
          isKnockout={submissionLogoIsKnockout(submission)}
                />
              </View>
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-semibold" numberOfLines={1}>
                  {submission.company_name}
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                  {submission.contact_email}
                </Text>
              </View>
              {/* Quick Info */}
              <View className="flex-row items-center gap-2 mr-2">
                {scorePill}
                {confidenceBadge}
                <StatusBadge
                  label={statusCfg.label}
                  color={statusCfg.color}
                  backgroundColor={statusCfg.bg}
                />
                {tags.map((t) => (
                  <StatusBadge
                    key={t.key}
                    label={t.label}
                    outlined
                    icon={Tag}
                    color={colors.primary[600]}
                    backgroundColor={colors.primary[50]}
                  />
                ))}
                {submission.status === 'scoring' && (
                  <ActivityIndicator size="small" color={colors.warning} />
                )}
              </View>
            </AnimatedPressable>
            <CardExpandChevron
              expanded={isExpanded}
              onToggle={() => onToggleExpanded(submission.id)}
              accessibilityLabel={isExpanded ? 'Collapse preview' : 'Expand preview'}
            />
          </View>

          {/* Expanded Content */}
          {isExpanded && (
            <View className="px-3 pb-3 border-t" style={{ borderColor: colors.border.light }}>
              {/* Sector / Stage / Ask */}
              <View className="flex-row flex-wrap gap-2 mt-3 mb-3">
                {submission.ai_extracted_data?.sector && (
                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                    <Text style={{ color: colors.text.secondary }} className="text-xs">
                      {submission.ai_extracted_data.sector}
                    </Text>
                  </View>
                )}
                {submission.ai_extracted_data?.stage && (
                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                    <Text style={{ color: colors.text.secondary }} className="text-xs capitalize">
                      {submission.ai_extracted_data.stage}
                    </Text>
                  </View>
                )}
                {submission.ai_extracted_data?.ask_amount && (
                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.primary[50] }}>
                    <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                      Ask: {formatAskAmount(submission.ai_extracted_data.ask_amount)}
                    </Text>
                  </View>
                )}
                {submission.ai_extracted_data?.team_size && (
                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                    <Text style={{ color: colors.text.secondary }} className="text-xs">
                      Team: {submission.ai_extracted_data.team_size}
                    </Text>
                  </View>
                )}
              </View>

              {/* One-liner */}
              {submission.ai_extracted_data?.one_liner && (
                <Text style={{ color: colors.text.secondary }} className="text-sm mb-3 italic">
                  &quot;{submission.ai_extracted_data.one_liner}&quot;
                </Text>
              )}

              {/* Score Breakdown + top risk — real data from the score_event */}
              {submission.startup_profile_id && (
                <CardScoreBreakdown
                  startupProfileId={submission.startup_profile_id}
                  variant="bars"
                  colors={colors}
                />
              )}

              {/* Timing. `flex-wrap` because the absolute stamp plus an expiry
                  is more than one line on a narrow phone, and `flex-1` +
                  numberOfLines on the stamp so it truncates rather than
                  pushing the expiry off the card. */}
              <View className="flex-row items-center flex-wrap gap-2 mb-3">
                <Clock size={12} color={colors.text.tertiary} />
                <Text
                  numberOfLines={1}
                  style={{ color: colors.text.tertiary }}
                  className="text-xs flex-1"
                >
                  Submitted {formatSubmittedAt(submission.created_at, 'full')}
                  {' · '}
                  {getRelativeTime(submission.created_at)}
                </Text>
                {expiry && (
                  <>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">{'\u00B7'}</Text>
                    <Text
                      style={{ color: expiry.isUrgent ? colors.error : colors.text.tertiary }}
                      className="text-xs font-medium"
                    >
                      {expiry.text}
                    </Text>
                  </>
                )}
              </View>

              {/* Actions — the two investor decisions. Archive is redundant with
                  Reject (which archives + tags), and Details is reached by tapping
                  the card. Each button fills half the row. */}
              <View className="flex-row gap-2">
                <CardActionButton
                  label="Reject"
                  variant="neutral"
                  icon={XCircle}
                  onPress={() => onReject(submission.id)}
                  accessibilityLabel={`Reject ${submission.company_name}`}
                />
                <CardActionButton
                  label="Shortlist"
                  variant="success"
                  icon={Star}
                  onPress={() => onShortlist(submission.id)}
                  accessibilityLabel={`Shortlist ${submission.company_name}`}
                />
              </View>
            </View>
          )}
        </View>
      </Animated.View>
    );
  }

  // ============================================================================
  // GRID MODE
  // ============================================================================
  return (
    <Animated.View
      entering={FadeIn.delay(100 + index * 30).duration(200)}
      style={{ width: '100%' }}
    >
      <AnimatedPressable
        onPress={() => onViewDetail(submission.id)}
        accessibilityRole="button"
        accessibilityLabel={`View ${submission.company_name}, score ${submission.ai_score ?? 'pending'}`}
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: isSelected ? 2 : 0,
          borderColor: isSelected ? colors.primary[600] : 'transparent',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.08,
          shadowRadius: 4,
          elevation: 3,
        }}
      >
        <View className="p-4">
          {/* Header Row */}
          <View className="flex-row items-start justify-between mb-3">
            <View className="flex-row items-center gap-3 flex-1">
              {isSelectionMode && (
                <AnimatedPressable
                  onPress={(e) => {
                    e.stopPropagation();
                    onToggleSelection(submission.id);
                  }}
                  accessibilityRole="checkbox"
                  accessibilityLabel={`Select ${submission.company_name}`}
                  accessibilityState={{ checked: isSelected }}
                >
                  {isSelected ? (
                    <CheckSquare size={24} color={colors.primary[600]} />
                  ) : (
                    <Square size={24} color={colors.text.tertiary} />
                  )}
                </AnimatedPressable>
              )}
              <CompanyAvatar
                name={submission.company_name}
                logoUrl={submissionLogoUrl(submission)}
          isKnockout={submissionLogoIsKnockout(submission)}
                size={56}
                radius={12}
              />
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-semibold text-base" numberOfLines={1}>
                  {submission.company_name}
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>
                  {submission.ai_extracted_data?.sector ?? 'Unknown'} {'\u2022'}{' '}
                  {submission.ai_extracted_data?.stage ?? 'N/A'}
                </Text>
              </View>
            </View>
            <View className="flex-row items-center gap-1.5 flex-wrap justify-end">
              <StatusBadge
                label={statusCfg.label}
                color={statusCfg.color}
                backgroundColor={statusCfg.bg}
              />
              {tags.map((t) => (
                <StatusBadge
                  key={t.key}
                  label={t.label}
                  outlined
                  icon={Tag}
                  color={colors.primary[600]}
                  backgroundColor={colors.primary[50]}
                />
              ))}
            </View>
          </View>

          {/* Score & Confidence */}
          <View className="flex-row items-center gap-3 mb-3">
            {scorePill}
            {confidenceBadge}
            {submission.ai_extracted_data?.ask_amount && (
              <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.primary[50] }}>
                <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                  Ask: {formatAskAmount(submission.ai_extracted_data.ask_amount)}
                </Text>
              </View>
            )}
          </View>

          {/* Score Breakdown mini-labels — real data from the score_event */}
          {submission.startup_profile_id && (
            <CardScoreBreakdown
              startupProfileId={submission.startup_profile_id}
              variant="inline"
              colors={colors}
            />
          )}

          {/* Timing, COMPACT.
              The grid card is the minimalist one and showed no timestamp at
              all. It gets the date and a clock time without the year or the
              relative phrase — enough to place the deck, short enough not to
              wrap. The list layout carries the full stamp plus "3 days ago";
              repeating all of that here is what would make this card stop
              being the compact one. */}
          <View className="flex-row items-center gap-1.5 mb-2">
            <Clock size={11} color={colors.text.tertiary} />
            <Text
              numberOfLines={1}
              style={{ color: colors.text.tertiary }}
              className="text-xs flex-1"
            >
              {formatSubmittedAt(submission.created_at, 'compact')}
            </Text>
          </View>

          {/* Expiry */}
          {expiry && (
            <View className="mb-2">
              <Text
                style={{ color: expiry.isUrgent ? colors.error : colors.text.tertiary }}
                className="text-xs"
              >
                {expiry.text}
              </Text>
            </View>
          )}
        </View>

        {/* Action Bar — same CardActionButton primitive as the list layout. */}
        <View className="flex-row gap-2 px-4 pb-4 pt-1 border-t" style={{ borderColor: colors.border.light }}>
          <CardActionButton
            label="Reject"
            variant="neutral"
            icon={XCircle}
            stopPropagation
            onPress={() => onReject(submission.id)}
            accessibilityLabel={`Reject ${submission.company_name}`}
          />
          <CardActionButton
            label="Shortlist"
            variant="success"
            icon={Star}
            stopPropagation
            onPress={() => onShortlist(submission.id)}
            accessibilityLabel={`Shortlist ${submission.company_name}`}
          />
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}
